"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { useState } from "react";
import { ArrowRight, Circle } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const seasons = [{
  value: "structural",
  label: "Estructurales"
}, {
  value: "tubular",
  label: "Tubulares"
}, {
  value: "plano",
  label: "Planos"
}, {
  value: "trefilado",
  label: "Trefilados"
}];
const sessions = [{
  id: 1,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/viga_acero_estructural.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/viga_acero_estructural.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/viga_acero_estructural.webp-wvc-sizes",
  alt: "Vigas de acero estructural de alta resistencia",
  day: "01",
  month: "Ene",
  year: "2025",
  format: "GRADO INDUSTRIAL",
  titleStart: "Vigas IPR y ",
  titleItalic: "Canales CPS",
  titleEnd: "",
  description: "Soporte fundamental para edificaciones de gran escala. Fabricadas bajo normas internacionales de calidad para garantizar la máxima seguridad estructural.",
  instructor: "Ing. Roberto Kalisch",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_roberto.png",
  avatarFallback: "RK",
  seatsLeft: 150,
  seatsTotal: 500,
  investment: "Consultar",
  soldOut: false,
  limited: true,
  season: "structural",
  offset: false
}, {
  id: 2,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/tuberia_industrial_acero.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/tuberia_industrial_acero.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/tuberia_industrial_acero.png-wvc-sizes",
  alt: "Tubería de acero industrial en almacén",
  day: "15",
  month: "Feb",
  year: "2025",
  format: "ALTA PRECISIÓN",
  titleStart: "Tubería de Cédula y ",
  titleItalic: "HSS",
  titleEnd: "",
  description: "Perfiles tubulares cuadrados y rectangulares para aplicaciones mecánicas y estructurales. Resistencia superior con acabados de primera.",
  instructor: "Ing. Roberto Kalisch",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_roberto.png",
  avatarFallback: "RK",
  seatsLeft: 80,
  seatsTotal: 200,
  investment: "Consultar",
  soldOut: false,
  limited: false,
  season: "tubular",
  offset: true
}, {
  id: 3,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/lamina_acero_plano.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/lamina_acero_plano.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/lamina_acero_plano.png-wvc-sizes",
  alt: "Láminas de acero plano galvanizado",
  day: "10",
  month: "Mar",
  year: "2025",
  format: "RECUBRIMIENTO ESPECIAL",
  titleStart: "Lámina en Rollo y ",
  titleItalic: "Placa",
  titleEnd: " — Grado A36",
  description: "Acero plano para procesos de transformación industrial. Disponibilidad inmediata en diversos calibres y acabados galvanizados o pintros.",
  instructor: "Ing. Elena Garza",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_elena.png",
  avatarFallback: "EG",
  seatsLeft: 0,
  seatsTotal: 300,
  investment: "Consultar",
  soldOut: true,
  limited: false,
  season: "plano",
  offset: false
}, {
  id: 4,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/alambre_trefilado_acero.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/alambre_trefilado_acero.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/alambre_trefilado_acero.png-wvc-sizes",
  alt: "Rollos de alambre trefilado",
  day: "22",
  month: "Abr",
  year: "2025",
  format: "MANUFACTURA",
  titleStart: "Alambres y Derivados ",
  titleItalic: "Trefilados",
  titleEnd: "",
  description: "Productos derivados del alambrón con procesos de estirado en frío. Ideal para la industria de la construcción y refuerzo de concreto.",
  instructor: "Ing. Alejandro Soto",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_alejandro.webp",
  avatarFallback: "AS",
  seatsLeft: 45,
  seatsTotal: 150,
  investment: "Consultar",
  soldOut: false,
  limited: true,
  season: "trefilado",
  offset: true
}, {
  id: 5,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placa_acero_gruesa.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placa_acero_gruesa.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/placa_acero_gruesa.png-wvc-sizes",
  alt: "Placa de acero de gran espesor",
  day: "05",
  month: "May",
  year: "2025",
  format: "CORTE A MEDIDA",
  titleStart: "Placa de Acero ",
  titleItalic: "Estructural",
  titleEnd: "",
  description: "Placas de alta resistencia para maquinaria pesada y cimentaciones. Servicio de corte con pantógrafo de alta definición disponible.",
  instructor: "Ing. Alejandro Soto",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_alejandro.webp",
  avatarFallback: "AS",
  seatsLeft: 12,
  seatsTotal: 50,
  investment: "Consultar",
  soldOut: false,
  limited: false,
  season: "structural",
  offset: false
}, {
  id: 6,
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/perfil_acero_comercial.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/perfil_acero_comercial.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/perfil_acero_comercial.png-wvc-sizes",
  alt: "Perfiles de acero comercial",
  day: "18",
  month: "Jun",
  year: "2025",
  format: "USO COMERCIAL",
  titleStart: "Ángulos y Soleras ",
  titleItalic: "Comerciales",
  titleEnd: " — Versatilidad",
  description: "Perfiles ligeros para herrería y estructuras menores. Calidad certificada Kalisch para proyectos residenciales e industriales.",
  instructor: "Ing. Elena Garza",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1679911/img/expert_elena.png",
  avatarFallback: "EG",
  seatsLeft: 25,
  seatsTotal: 100,
  investment: "Consultar",
  soldOut: false,
  limited: true,
  season: "structural",
  offset: true
}];
export default function SubProductsGrid() {
  const [activeSeason, setActiveSeason] = useState<string>("");
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const filtered = activeSeason ? sessions.filter(s => s.season === activeSeason) : sessions;
  return <section data-section-id="3046" id="sub-products-grid" className="bg-background text-foreground py-24 md:py-32 lg:py-44">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-6 lg:col-start-2">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-6">
              Catálogo — Soluciones 2025
            </p>
            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground">
              Acero de calidad para la{" "}
              <span className="italic">industria moderna.</span>
            </h2>
          </div>

          <div className="lg:col-span-3 lg:col-start-9 flex lg:justify-end">
            <ToggleGroup type="single" value={activeSeason} onValueChange={v => setActiveSeason(v)} variant="outline" className="flex-wrap gap-2 rounded-lg border-0">
              {seasons.map((s, i) => <ToggleGroupItem key={s.value} data-index={i} value={s.value} className="font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2 rounded-lg border border-border first:rounded-lg last:rounded-lg data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary text-foreground hover:bg-muted">
                  {s.label}
                </ToggleGroupItem>)}
            </ToggleGroup>
          </div>
        </div>

        <div className="mt-20 lg:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
          {filtered.map((session, i) => {
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15,
            rootMargin: "0px 0px -50px 0px"
          });
          return <div key={session.id} data-index={i} ref={card.ref} className={`transition-all duration-[900ms] ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${session.offset ? "lg:mt-6" : ""}`} style={{
            transitionDelay: `${i % 3 * 120}ms`
          }}>
                <Card className={`group h-full border border-border bg-card rounded-lg p-0 gap-0 transition-all duration-[800ms] ease-out hover:-translate-y-2 hover:shadow-lg ${session.soldOut ? "opacity-90" : ""}`}>
                  <CardContent className="p-8 lg:p-10 flex flex-col h-full">
                    <div className="relative overflow-hidden rounded-md aspect-[4/3] mb-8">
                      <img src={session.image} data-wvc-srcset={session.srcset} data-wvc-sizes={session.sizes} alt={session.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 ${session.soldOut ? "grayscale" : ""}`} />
                      {session.soldOut && <div className="absolute inset-0 flex items-start justify-end p-4">
                          <Badge className="bg-secondary text-secondary-foreground border border-secondary font-default italic text-sm px-3 py-1 rounded-md">
                            Agotado
                          </Badge>
                        </div>}
                    </div>

                    <div className="flex items-baseline gap-3 mb-5">
                      <span className="font-default text-2xl text-foreground">
                        {session.day} {session.month}
                      </span>
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                        {session.year}
                      </span>
                    </div>

                    <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-accent mb-4">
                      {session.format}
                    </p>

                    <h3 className="font-default text-2xl lg:text-[28px] leading-[1.2] text-foreground mb-4">
                      {session.titleStart}
                      <span className="italic">{session.titleItalic}</span>
                      {session.titleEnd}
                    </h3>

                    <p className="font-default text-base text-muted-foreground leading-relaxed mb-7 line-clamp-3">
                      {session.description}
                    </p>

                    <div className="flex items-center gap-3 mb-7">
                      <SafeHydrate><Avatar className="size-8 rounded-md">
                        <AvatarImage src={session.avatar} alt={session.instructor} />
                        <AvatarFallback className="bg-muted text-foreground text-xs font-mono">
                          {session.avatarFallback}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                      <p className="font-default italic text-sm text-foreground">
                        Asesoría por {session.instructor}
                      </p>
                    </div>

                    <div className="mt-auto">
                      <Separator className="mb-5 bg-border" />
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">
                            Disponibilidad
                          </p>
                          <p className="font-default text-sm text-foreground flex items-center gap-2">
                            {session.limited && !session.soldOut && <Circle className="size-2 fill-accent text-accent" />}
                            {session.soldOut ? "Sin existencias" : `Stock: ${session.seatsLeft} ton`}
                          </p>
                        </div>
                        <div>
                          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">
                            Cotización
                          </p>
                          <p className="font-default text-sm text-foreground">
                            {session.investment}
                          </p>
                        </div>
                      </div>

                      <Button asChild className="w-full rounded-md bg-primary text-primary-foreground hover:bg-primary-600 font-mono text-[11px] tracking-[0.2em] uppercase h-12">
                        <Link to="/CatalogPage">
                          {session.soldOut ? "Notificar Stock" : "Solicitar Cotización"}
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        <div className="mt-24 flex items-center justify-center gap-6">
          <Separator className="flex-1 bg-border" />
          <span className="font-default italic text-xl text-muted-foreground">
            &mdash;
          </span>
          <Separator className="flex-1 bg-border" />
        </div>
      </div>
    </section>;
}